// src/lib/api/adminApi.ts
import { getApiClient } from './client';
import {
    SystemStats,
    OrganisationWithCounts,
    CompleteOrganisationDetails,
    CreateCompleteEmployerRequest,
    CreateOrganisationRequest,
    CreateUnitRequest,
    CreateEmployerRequest,
} from '../models/admin';
import {
    Organisation,
    Unit,
    UpdateOrganisationRequest,
    UpdateUnitRequest,
    UpdateEmployerRequest,
    EmployerWithDetails,
} from '../models/employer';
import { Contact, ContactStatus } from '../models/contact';

// ===================================
// ADMIN DASHBOARD ENDPOINTS
// ===================================

/**
 * Get system statistics (total/active counts)
 */
const getSystemStats = async (): Promise<SystemStats> => {
    return getApiClient().get<SystemStats>('/api/admin/stats');
};

/**
 * List all organisations with unit and employer counts
 */
// #353 — same swallow as listUsers below, same fix. AdminContext.loadOrganisations
// already sets organisationsError; catching here made "the request failed" and
// "there are no organisations" the same screen.
const listOrganisationsWithCounts = async (): Promise<OrganisationWithCounts[]> => {
    return await getApiClient().get<OrganisationWithCounts[]>('/api/admin/organisations', {
        params: { limit: 100 },
    });
};

/**
 * Get complete organisation details (organisation + all units + all employers)
 */
const getCompleteOrganisation = async (
    organisationId: string
): Promise<CompleteOrganisationDetails> => {
    return getApiClient().get<CompleteOrganisationDetails>(
        `/api/admin/organisations/${organisationId}/complete`
    );
};

// ===================================
// ORGANISATION CRUD (Admin Only)
// ===================================

/**
 * Create a new organisation
 */
const createOrganisation = async (data: CreateOrganisationRequest): Promise<Organisation> => {
    return getApiClient().post<Organisation>('/api/organisations', data);
};

/**
 * Update an organisation
 */
const updateOrganisation = async (
    organisationId: string,
    data: UpdateOrganisationRequest
): Promise<Organisation> => {
    return getApiClient().put<Organisation>(`/api/organisations/${organisationId}`, data);
};

/**
 * Delete an organisation
 */
const deleteOrganisation = async (organisationId: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/organisations/${organisationId}`);
};

/**
 * Upload an organisation logo (#40). Returns the updated org (with logo_url set).
 */
const uploadOrganisationLogo = async (organisationId: string, file: File): Promise<Organisation> => {
    const formData = new FormData();
    formData.append('file', file);
    return getApiClient().uploadMultipart<Organisation>(`/api/organisations/${organisationId}/logo`, formData, 'post');
};

// ===================================
// UNIT CRUD (Admin Only)
// ===================================

/**
 * List all units (across all organisations).
 */
const listUnits = async (): Promise<Unit[]> => {
    return getApiClient().get<Unit[]>('/api/units', { params: { limit: 100 } });
};

/**
 * Create a new unit
 */
const createUnit = async (data: CreateUnitRequest): Promise<Unit> => {
    return getApiClient().post<Unit>('/api/units', data);
};

/**
 * Update a unit
 */
const updateUnit = async (unitId: string, data: UpdateUnitRequest): Promise<Unit> => {
    return getApiClient().put<Unit>(`/api/units/${unitId}`, data);
};

/**
 * Delete a unit
 */
const deleteUnit = async (unitId: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/units/${unitId}`);
};

// ===================================
// EMPLOYER CRUD (Admin Only)
// ===================================

/**
 * Create complete employer setup (org + unit + user + employer)
 */
const createCompleteEmployer = async (
    data: CreateCompleteEmployerRequest
): Promise<EmployerWithDetails> => {
    return getApiClient().post<EmployerWithDetails>('/api/employers/complete', data);
};

/**
 * Create employer for existing unit
 */
const createEmployer = async (data: CreateEmployerRequest): Promise<EmployerWithDetails> => {
    return getApiClient().post<EmployerWithDetails>('/api/employers', data);
};

/**
 * Update employer
 */
const updateEmployer = async (
    employerId: string,
    data: UpdateEmployerRequest
): Promise<EmployerWithDetails> => {
    return getApiClient().put<EmployerWithDetails>(`/api/employers/${employerId}`, data);
};

/**
 * Delete employer
 */
const deleteEmployer = async (employerId: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/employers/${employerId}`);
};

// ===================================
// USER MANAGEMENT
// ===================================

/**
 * List all users with optional role filter
 */
// #353 — deliberately does NOT catch.
//
// It used to swallow every failure into `[]`. AdminContext.loadUsers has a
// complete error path (setUsersError, rendered by the page), and this catch sat
// in front of it, so that path could never run: a 500, an expired token or a
// dead backend all arrived as an empty array and rendered as "0 användare" —
// visually identical to a database with no users in it. The admin then either
// believes the platform is empty or reports a bug nobody can reproduce.
//
// Errors belong to whoever can show them. This function has no UI.
const listUsers = async (role?: string): Promise<any[]> => {
    const params: any = { limit: 100 };
    if (role) {
        params.role = role;
    }
    return await getApiClient().get<any[]>('/api/admin/users', { params });
};

/**
 * Get user statistics by role
 */
const getUserStats = async (): Promise<any> => {
    return getApiClient().get<any>('/api/admin/users/stats');
};

// ===================================
// CONTACT MESSAGES (Admin Only)
// ===================================

/**
 * List all contact form submissions, optionally filtered by status.
 * Errors propagate to the caller — the page renders an error state on failure.
 */
// ==================== EMPLOYER INVITATIONS (#358) ====================

/** An employer account that exists but whose owner has never signed in. */
export interface PendingEmployerInvitation {
    id: string;
    email: string;
    organisation_id: string;
    organisation_name: string;
    unit_name: string;
    invited_at: string;
    expires_at: string;
    is_expired: boolean;
}

// Every admin-created employer who has not chosen a password yet, oldest first.
// This is the admin home queue: each row is a person who cannot get in, and
// resendEmployerInvitation is the fix.
const listPendingEmployerInvitations = async (): Promise<PendingEmployerInvitation[]> => {
    const res = await getApiClient().get<{ invitations: PendingEmployerInvitation[] }>(
        '/api/admin/employer-invitations'
    );
    return res?.invitations ?? [];
};

// Issues a FRESH token — the old link stops working — and re-sends the mail.
const resendEmployerInvitation = async (invitationId: string): Promise<void> => {
    await getApiClient().post<{ message: string }>(
        `/api/admin/employer-invitations/${invitationId}/resend`,
        {}
    );
};

const listContacts = async (status?: ContactStatus): Promise<Contact[]> => {
    if (status) {
        return getApiClient().get<Contact[]>(`/api/contacts/status/${status}`, {
            params: { limit: 100 },
        });
    }
    return getApiClient().get<Contact[]>('/api/contacts', {
        params: { limit: 100 },
    });
};

/**
 * Get a single contact submission by ID.
 */
const getContactById = async (id: string): Promise<Contact> => {
    return getApiClient().get<Contact>(`/api/contacts/${id}`);
};

/**
 * Update the status of a contact submission (new ↔ contacted ↔ closed).
 */
const updateContactStatus = async (id: string, status: ContactStatus): Promise<Contact> => {
    return getApiClient().put<Contact>(`/api/contacts/${id}/status`, { status });
};

/**
 * Permanently delete a contact submission. Admin only.
 */
const deleteContact = async (id: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/contacts/${id}`);
};

// ===================================
// EXPORTS
// ===================================

export const adminApi = {
    listPendingEmployerInvitations,
    resendEmployerInvitation,
    // Dashboard
    getSystemStats,
    listOrganisationsWithCounts,
    getCompleteOrganisation,

    // Organisation CRUD
    createOrganisation,
    updateOrganisation,
    deleteOrganisation,
    uploadOrganisationLogo,

    // Unit CRUD
    listUnits,
    createUnit,
    updateUnit,
    deleteUnit,

    // Employer CRUD
    createCompleteEmployer,
    createEmployer,
    updateEmployer,
    deleteEmployer,

    // User Management
    listUsers,
    getUserStats,

    // Contact Messages
    listContacts,
    getContactById,
    updateContactStatus,
    deleteContact,
};
